import { Container } from '@/components/Container';
import { useTranslation } from '@/hooks/useTranslation';

/* ═══════════════════════════════════════════
   MAPA MENTAL — Diagrama CSS da Ilha do Mel
   Clique para navegar direto à seção
   ═══════════════════════════════════════════ */

const vilarejosData = [
  {
    id: 'nova-brasilia',
    nomeKey: 'guide.mapaMental.village.novaBrasilia',
    tagKey: 'guide.mapaMental.tagNorte',
    cor: 'bg-teal-50 border-teal-200',
    corTexto: 'text-teal-deep',
    destaquesKeys: [
      'guide.mapaMental.nbHighlight1',
      'guide.mapaMental.nbHighlight2',
      'guide.mapaMental.nbHighlight3',
      'guide.mapaMental.nbHighlight4',
    ],
    secao: 'onde-ficar',
  },
  {
    id: 'encantadas',
    nomeKey: 'guide.mapaMental.village.encantadas',
    tagKey: 'guide.mapaMental.tagSul',
    cor: 'bg-coral/5 border-coral/20',
    corTexto: 'text-coral',
    destaquesKeys: [
      'guide.mapaMental.encHighlight1',
      'guide.mapaMental.encHighlight2',
      'guide.mapaMental.encHighlight3',
      'guide.mapaMental.encHighlight4',
    ],
    secao: 'onde-ficar',
  },
  {
    id: 'centro',
    nomeKey: 'guide.mapaMental.village.centro',
    tagKey: 'guide.mapaMental.tagMeio',
    cor: 'bg-sand-50 border-sand-200',
    corTexto: 'text-warm-dark',
    destaquesKeys: [
      'guide.mapaMental.centroHighlight1',
      'guide.mapaMental.centroHighlight2',
      'guide.mapaMental.centroHighlight3',
      'guide.mapaMental.centroHighlight4',
    ],
    secao: 'roteiros',
  },
];

const conexoesData = [
  { from: 'nova-brasilia', to: 'centro', tempo: '20 min', tipoKey: 'guide.mapaMental.trailType' },
  { from: 'centro', to: 'encantadas', tempo: '40 min', tipoKey: 'guide.mapaMental.trailType' },
  { from: 'nova-brasilia', to: 'encantadas', tempo: '1h', tipoKey: 'guide.mapaMental.directTrail' },
];

export function MapaMentalSection() {
  const { t } = useTranslation();
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const vilarejos = vilarejosData.map(v => ({
    ...v,
    nome: t(v.nomeKey),
    tag: t(v.tagKey),
    destaques: v.destaquesKeys.map(dk => t(dk)),
  }));

  const conexoes = conexoesData.map(c => ({
    ...c,
    tipo: t(c.tipoKey),
  }));

  return (
    <section id="mapa" className="py-16 sm:py-20 bg-white">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.mapaMental.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.mapaMental.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.mapaMental.subtitle')}
          </p>
        </div>

        {/* Mapa visual */}
        <div className="max-w-4xl mx-auto">
          {/* Vilarejos */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {vilarejos.map(v => (
              <button
                key={v.id}
                onClick={() => scrollTo(v.secao)}
                className={`text-left rounded-2xl border-2 p-6 ${v.cor} hover:shadow-lg transition-all hover:-translate-y-1 group cursor-pointer`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`font-body text-xs font-bold uppercase tracking-wider ${v.corTexto}`}
                  >
                    {v.tag}
                  </span>
                  <svg
                    className="w-5 h-5 text-sand-300 group-hover:text-teal-deep transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </div>
                <h3 className="font-display text-xl text-warm-dark mb-3">{v.nome}</h3>
                <div className="space-y-1.5">
                  {v.destaques.map(d => (
                    <div key={d} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                      <span className="font-body text-sm text-warm-gray">{d}</span>
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>

          {/* Conexões */}
          <div className="bg-sand-50 rounded-2xl border border-sand-200 p-6">
            <h3 className="font-display text-lg text-warm-dark mb-4 text-center">
              {t('guide.mapaMental.distancesTitle')}
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {conexoes.map(c => (
                <div
                  key={c.from + c.to}
                  className="bg-white rounded-xl border border-sand-200 p-4 text-center"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="font-body text-sm font-semibold text-warm-dark">
                      {vilarejos.find(v => v.id === c.from)?.nome.split(' ')[0]}
                    </span>
                    <svg
                      className="w-4 h-4 text-sand-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                    <span className="font-body text-sm font-semibold text-warm-dark">
                      {vilarejos.find(v => v.id === c.to)?.nome.split(' ')[0]}
                    </span>
                  </div>
                  <div className="font-display text-2xl text-teal-deep">{c.tempo}</div>
                  <div className="font-body text-xs text-warm-gray">{c.tipo}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Legenda */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 font-body text-sm text-warm-gray">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-teal-400" />
              {t('guide.mapaMental.legendStructure')}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-coral" />
              {t('guide.mapaMental.legendCalm')}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-sand-300" />
              {t('guide.mapaMental.legendPreservation')}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
